import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { UserPhoneNumberOption } from '../types/inbox';

interface UseUserPhoneNumbersOptions {
  smsOnly?: boolean;
  voiceOnly?: boolean;
  autoFetch?: boolean;
}

export const useUserPhoneNumbers = (options: UseUserPhoneNumbersOptions = {}) => {
  const { smsOnly = false, voiceOnly = false, autoFetch = true } = options;
  const [phoneNumbers, setPhoneNumbers] = useState<UserPhoneNumberOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (smsOnly) params.append('sms_only', 'true');
      if (voiceOnly) params.append('voice_only', 'true');

      const queryString = params.toString();
      const url = `/api/inbox/phone-number-assignments/my-numbers/${queryString ? `?${queryString}` : ''}`;

      const response = await api.get<UserPhoneNumberOption[]>(url);
      setPhoneNumbers(response.data);
    } catch (err) {
      setError('Failed to load phone numbers');
      setPhoneNumbers([]);
    } finally {
      setLoading(false);
    }
  }, [smsOnly, voiceOnly]);

  useEffect(() => {
    if (autoFetch) {
      fetchPhoneNumbers();
    }
  }, [autoFetch, fetchPhoneNumbers]);

  // Get the default phone number
  const defaultNumber = phoneNumbers.find((p) => p.is_default);

  // Get a specific phone number by ID
  const getPhoneNumberById = useCallback(
    (id: number) => phoneNumbers.find((p) => p.phone_number_id === id),
    [phoneNumbers]
  );

  return {
    phoneNumbers,
    defaultNumber,
    loading,
    error,
    refetch: fetchPhoneNumbers,
    getPhoneNumberById,
    hasPhoneNumbers: phoneNumbers.length > 0,
  };
};
